import React from "react";

/**
 * Measurement-based column auto-fit for DataTableV2.
 *
 * Each cell (header + body) that opts in reports the natural, unconstrained
 * width of its content for a given column key. The provider keeps the maximum
 * reported width per column and exposes a resolved width that is clamped to
 * [MIN_AUTO_FIT_WIDTH, MAX_AUTO_FIT_WIDTH]. Cells then apply that resolved
 * width, so every column shrinks to its content while never exceeding the cap.
 *
 * This is intentionally decoupled from the manual-resize persistence
 * (`masterColumnResize`): auto-fit only ever applies to columns that have no
 * explicit `width` and no stored manual-resize value.
 */

// Horizontal padding/safety added to the measured content width so text is not
// clipped (cells have a left padding of theme spacing(2) == 16px, plus a buffer
// for sort icons / bold header font / sub-pixel rounding).
export const AUTO_FIT_CELL_PADDING = 34;

// Lower bound keeps very short columns usable/clickable.
export const MIN_AUTO_FIT_WIDTH = 60;

// Hard upper bound requested by product: columns never grow past 300px.
export const MAX_AUTO_FIT_WIDTH = 300;

interface IAutoFitContextValue {
  enabled: boolean;
  /** Resolved, clamped width (px) for a column key, or undefined if not measured yet. */
  getResolvedWidth: (columnKey: string) => number | undefined;
  /** Report a measured natural content width for a column key from a single cell. */
  report: (
    columnKey: string,
    cellId: string,
    naturalWidth: number,
    isHeader?: boolean
  ) => void;
  /** Remove a cell's contribution (on unmount / data change). */
  unreport: (columnKey: string, cellId: string) => void;
}

const AutoFitContext = React.createContext<IAutoFitContextValue>({
  enabled: false,
  getResolvedWidth: () => undefined,
  report: () => undefined,
  unreport: () => undefined,
});

export const useAutoFitContext = () => React.useContext(AutoFitContext);

const clamp = (value: number) =>
  Math.max(MIN_AUTO_FIT_WIDTH, Math.min(MAX_AUTO_FIT_WIDTH, value));

/**
 * Provider that aggregates per-cell measurements into a resolved width per
 * column. `resetKey` should change whenever the row set changes (e.g. page or
 * data change) so stale measurements from previous data are discarded.
 */
export const AutoFitColumnsProvider: React.FC<{
  enabled: boolean;
  resetKey?: string | number;
  children: React.ReactNode;
}> = ({ enabled, resetKey, children }) => {
  // columnKey -> (cellId -> naturalWidth)
  const measurementsRef = React.useRef<
    Record<string, Record<string, number>>
  >({});
  // columnKey -> header natural width (floor so headers are never clipped).
  const headerWidthsRef = React.useRef<Record<string, number>>({});
  const [resolvedWidths, setResolvedWidths] = React.useState<
    Record<string, number>
  >({});
  const rafRef = React.useRef<number | null>(null);
  // Holds the latest recompute fn so effects declared before it can call it.
  const recomputeRef = React.useRef<() => void>(() => undefined);

  // Discard stale BODY measurements when the underlying data changes (page or
  // data swap). Header widths are intentionally preserved: a header's size does
  // not depend on the row data, and its ResizeObserver will not re-fire on a
  // data change (the header node size is unchanged), so wiping header widths
  // here would leave them permanently at 0 after the first data load — which
  // caused headers to be clipped to the (shorter) body-content width.
  React.useEffect(() => {
    measurementsRef.current = {};
    // Keep headerWidthsRef as-is. Recompute so resolved widths reflect the
    // header floor even before fresh body measurements arrive.
    setResolvedWidths((prev) => prev);
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => recomputeRef.current());
    }
  }, [resetKey]);

  // When auto-fit is toggled off/on entirely, reset everything.
  React.useEffect(() => {
    if (!enabled) {
      measurementsRef.current = {};
      headerWidthsRef.current = {};
      setResolvedWidths({});
    }
  }, [enabled]);

  const recompute = React.useCallback(() => {
    rafRef.current = null;
    const next: Record<string, number> = {};
    const all = measurementsRef.current;
    const headers = headerWidthsRef.current;
    const columnKeys = new Set<string>([
      ...Object.keys(all),
      ...Object.keys(headers),
    ]);
    columnKeys.forEach((columnKey) => {
      const cells = all[columnKey] || {};
      let max = 0;
      for (const cellId in cells) {
        if (cells[cellId] > max) {
          max = cells[cellId];
        }
      }
      const contentWidth = max > 0 ? Math.ceil(max + AUTO_FIT_CELL_PADDING) : 0;
      // The header width is a hard floor: the column must always be wide enough
      // to show the full header name (also padded), even when body content is
      // shorter (e.g. "--"). The upper 300px cap still applies via clamp; if a
      // header is wider than the cap the header text wraps.
      const headerWidth = headers[columnKey]
        ? Math.ceil(headers[columnKey] + AUTO_FIT_CELL_PADDING)
        : 0;
      const target = Math.max(contentWidth, headerWidth);
      if (target > 0) {
        next[columnKey] = clamp(target);
      }
    });
    setResolvedWidths((prev) => {
      // Avoid pointless re-renders when nothing changed.
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        let same = true;
        for (const k of nextKeys) {
          if (prev[k] !== next[k]) {
            same = false;
            break;
          }
        }
        if (same) {
          return prev;
        }
      }
      return next;
    });
  }, []);

  // Keep the ref pointed at the latest recompute implementation.
  recomputeRef.current = recompute;

  const scheduleRecompute = React.useCallback(() => {
    if (rafRef.current != null) {
      return;
    }
    rafRef.current = requestAnimationFrame(recompute);
  }, [recompute]);

  const report = React.useCallback(
    (
      columnKey: string,
      cellId: string,
      naturalWidth: number,
      isHeader?: boolean
    ) => {
      if (!enabled) {
        return;
      }
      if (isHeader) {
        if (headerWidthsRef.current[columnKey] === naturalWidth) {
          return;
        }
        headerWidthsRef.current[columnKey] = naturalWidth;
        scheduleRecompute();
        return;
      }
      const all = measurementsRef.current;
      if (!all[columnKey]) {
        all[columnKey] = {};
      }
      if (all[columnKey][cellId] === naturalWidth) {
        return;
      }
      all[columnKey][cellId] = naturalWidth;
      scheduleRecompute();
    },
    [enabled, scheduleRecompute]
  );

  const unreport = React.useCallback(
    (columnKey: string, cellId: string) => {
      const all = measurementsRef.current;
      if (all[columnKey] && all[columnKey][cellId] !== undefined) {
        delete all[columnKey][cellId];
        scheduleRecompute();
      }
    },
    [scheduleRecompute]
  );

  React.useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const value = React.useMemo<IAutoFitContextValue>(
    () => ({
      enabled,
      getResolvedWidth: (columnKey: string) => resolvedWidths[columnKey],
      report,
      unreport,
    }),
    [enabled, resolvedWidths, report, unreport]
  );

  return (
    <AutoFitContext.Provider value={value}>{children}</AutoFitContext.Provider>
  );
};

/**
 * Measures the natural (content) width of a cell and reports it to the auto-fit
 * provider. Measurement is done against the inner content node, which is
 * rendered with `width: max-content` so it reflects the true content size
 * regardless of the outer cell's applied width.
 *
 * Returns a ref to attach to the inner content node.
 */
export const useAutoFitCellMeasure = (
  columnKey: string | undefined,
  active: boolean,
  isHeader?: boolean
) => {
  const { report, unreport } = useAutoFitContext();
  const generatedId = React.useId();
  const cellIdRef = React.useRef<string>(`cell_${generatedId}`);
  const nodeRef = React.useRef<HTMLDivElement | null>(null);

  const measure = React.useCallback(() => {
    if (!active || !columnKey || !nodeRef.current) {
      return;
    }
    // getBoundingClientRect reflects the intrinsic width of the inline-flex
    // measuring node (which is not stretched by the cell's min-width), and
    // scrollWidth guards against any sub-pixel clipping. Take the larger.
    const rectWidth = nodeRef.current.getBoundingClientRect().width;
    const naturalWidth = Math.max(rectWidth, nodeRef.current.scrollWidth);
    report(columnKey, cellIdRef.current, naturalWidth, isHeader);
  }, [active, columnKey, report, isHeader]);

  React.useLayoutEffect(() => {
    if (!active || !columnKey) {
      return;
    }
    measure();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && nodeRef.current) {
      observer = new ResizeObserver(() => measure());
      observer.observe(nodeRef.current);
    }
    const cellId = cellIdRef.current;
    return () => {
      if (observer) {
        observer.disconnect();
      }
      unreport(columnKey, cellId);
    };
  }, [active, columnKey, measure, unreport]);

  return nodeRef;
};
